import { FcWidget } from './models/fc-widget';
import { BrazeEventsService } from '../../services/braze-events.service';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgIf } from '@angular/common';
import { noop } from '@tmx/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { environmentProvider } from '@shared/environment-provider';
import { getCurrentAuthenticatedUser } from '@tmx/auth';

@Component({
  selector: 'support-chat',
  templateUrl: './support-chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, TooltipModule, TranslocoModule],
})
export class SupportChatComponent implements OnDestroy {
  private readonly brazeEventsService = inject(BrazeEventsService);
  private readonly translocoService = inject(TranslocoService);
  private fcWidget: FcWidget | undefined = undefined;

  isFacade = signal(true);
  disabled = signal(true);
  tooltipText = this.translocoService.translate(
    'components.supportChat.availableToolTip'
  );

  initFreshchat = () => {
    this.initFreshchat = noop;

    const script = document.createElement('script');
    script.setAttribute(
      'src',
      environmentProvider.getEnvironment().accountManagerSite.freshchat
        .widgetUrl
    );

    const makeUnavailable = () => {
      this.tooltipText = this.translocoService.translate(
        'components.supportChat.unavailableToolTip'
      );
      this.isFacade.set(false);
    };

    script.onerror = makeUnavailable;

    script.onload = () => {
      this.fcWidget = (window as unknown as { fcWidget?: FcWidget }).fcWidget;
      if (!this.fcWidget) {
        makeUnavailable();
        return;
      }

      this.fcWidget.init({
        ...environmentProvider.getEnvironment().accountManagerSite.freshchat
          .settings,
        externalId: getCurrentAuthenticatedUser().userId,
      });

      this.fcWidget.on('widget:loaded', () => {
        this.tooltipText = this.translocoService.translate(
          'components.supportChat.availableToolTip'
        );
        this.disabled.set(false);

        if (this.isFacade()) {
          this.removeFacade = noop;
          this.isFacade.set(false);
          return;
        }

        this.fcWidget?.open();
      });
    };

    document.body.appendChild(script);
  };

  removeFacade = () => {
    this.removeFacade = noop;
    this.isFacade.set(false);

    this.tooltipText = this.translocoService.translate(
      'components.supportChat.connectingToolTip'
    );
  };

  toggleShowChat() {
    if (!this.fcWidget) {
      return;
    }

    if (this.fcWidget.isOpen()) {
      this.fcWidget.close();
      return;
    }

    this.brazeEventsService.logBrazeCustomEvents(
      this.brazeEventsService.OPENED_FRESHCHAT
    );

    this.fcWidget.user
      .setProperties({
        firstName: getCurrentAuthenticatedUser().firstName,
        lastName: getCurrentAuthenticatedUser().lastName,
        email: getCurrentAuthenticatedUser().username,
        account: getCurrentAuthenticatedUser().accountName,
      })
      .then(() => {
        this.fcWidget?.open();
      });
  }

  ngOnDestroy() {
    this.fcWidget?.destroy();
  }
}
